import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, Inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { DateUtil, DisplayService, FieldInputType, TextUtil, ValidationUtil } from '@qid/core';
import { IonIcon, IonList, IonItem, IonLabel, IonRadio, IonRadioGroup, ModalController } from '@ionic/angular/standalone';
import { DatePipe, CommonModule } from '@angular/common';
import CountryList from 'country-list-with-dial-code-and-flag'
import { ENVIRONMENT, Environment } from '@qid/env';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { FormsModule } from '@angular/forms';
import { SexyModule } from '@qid/sexy';
import { SuperSelectComponent } from '../super-select/super-select.component';

// Lazy import to avoid circular dependency
const SuperInputModalComponent = () => import('../super-input-modal/super-input-modal.component').then(m => m.SuperInputModalComponent);

// The dial-code list is static; compute it once for the whole app instead of on
// every change-detection pass of every phone field.
let cachedDialCodes: string[] | null = null;
function getDialCodes(): string[] {
  if (!cachedDialCodes) {
    cachedDialCodes = CountryList.getAll().map((data) => data.dial_code);
  }
  return cachedDialCodes;
}

@Component({
  selector: 'super-input',
  templateUrl: './super-input.component.html',
  styleUrls: ['./super-input.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    IonIcon,
    IonList,
    IonItem,
    IonLabel,
    IonRadio,
    IonRadioGroup,
    SexyModule
  ],
  providers: [DatePipe],
})
export class SuperInputComponent implements OnInit, OnChanges {
  @Input() label?: string | null = '';
  @Input() iconStart: string;
  @Input() readonly: boolean = false;
  @Input() checkBoxRequired = false;
  @Input() helpNote: string | null = null;
  @Input() placeholder: string = '';
  @Input() type: FieldInputType | string = FieldInputType.text;
  @Input() options: any;
  @Input() dropDownLabelKey: string;
  @Input() required: boolean = false;
  @Input() validate: boolean = true;
  /** When true (default), empty time fields are initialized to the current time. Gateway and similar flows should set false. */
  @Input() prefillEmptyTimeWithNow = true;
  @Input() errorNote: string | null = null;
  @Input() showCountrySelector: boolean = false;
  @Input() fixedCountry: string = null;
  @Input() fieldCallback: ((value: any) => void) | null = null;
  @Output() errorNoteChange: EventEmitter<any> = new EventEmitter<any>();

  @Input() countryCode: string = '+91';
  @Output() countryCodeChange: EventEmitter<any> = new EventEmitter<any>();

  @Input() value: any;
  @Output() valueChange: EventEmitter<any> = new EventEmitter<any>();

  selectedInputType = 'input';
  isFocused: boolean = false;

  phoneInput: string = '';

  // Segmented date field state. Canonical `value` stays a `yyyy-MM-dd` string.
  dateDay = '';
  dateMonth = '';
  dateYear = '';

  // Stable ids so the label/error/help can be associated with the control for a11y.
  private static nextId = 0;
  inputId = `super-input-${SuperInputComponent.nextId++}`;
  describedById = `${this.inputId}-note`;

  // Derived view state, precomputed so the template never calls methods during
  // change detection.
  displayOptions: any[] = [];
  showDialCodeSelector = false;
  showDropdown = false;
  isLargeList = false;
  selectedLabel: any;

  private initialized = false;
  private datePipe = inject(DatePipe);
  private cdr = inject(ChangeDetectorRef);

  constructor(
    private displayService: DisplayService,
    private modalController: ModalController,

    @Inject(ENVIRONMENT) public env: Environment
  ) {
  }

  ngOnChanges(changes: SimpleChanges) {
    // The phone field renders from internal state (`phoneInput` / `countryCode`) that is only
    // derived from `value` in `ngOnInit`. When the bound `value` changes programmatically
    // afterwards (e.g. switching the active business), re-derive it so the field reflects the
    // new value. Skip while the user is editing to avoid clobbering their input / cursor.
    if (changes['value'] && !changes['value'].firstChange && !this.isFocused) {
      if (this.type === FieldInputType.phone) {
        this.splitPhoneNumber();
        this.cdr.markForCheck();
      } else if (this.type === FieldInputType.date) {
        this.splitDate();
        this.cdr.markForCheck();
      }
    }

    // Recompute derived view state when an input that feeds it changes. First change
    // is handled by ngOnInit (which also runs setType first).
    if (this.initialized) {
      if (changes['options'] || changes['type'] || changes['checkBoxRequired']) {
        this.computeOptions();
      }
      if (changes['value'] || changes['dropDownLabelKey']) {
        this.computeSelectedLabel();
      }
    }
  }

  ngOnInit() {
    this.setType();
    if (this.type === FieldInputType.date) {
      // Only normalise an existing value (filled by OCR or user). Never auto-fill
      // with today's date - an empty field must stay empty.
      if (!ValidationUtil.isStringEmpty(this.value)) {
        const date = new Date(this.value);
        if (!isNaN(date.getTime())) {
          this.value = this.datePipe.transform(date, 'yyyy-MM-dd');
          this.onValueChange();
        }
      }
      this.splitDate();
    }
    this.computeOptions();
    this.computeSelectedLabel();
    this.initialized = true;
  }

  setType() {
    if (this.type === FieldInputType.textarea) {
      return this.selectedInputType = 'textarea';
    }

    if (this.type === FieldInputType.country || this.type === FieldInputType.dropdown) {
      return this.selectedInputType = 'dropdown';
    }

    if (this.type === FieldInputType.phone) {
      this.splitPhoneNumber();
      return this.selectedInputType = 'phone';
    }

    if (this.type === FieldInputType.date) {
      this.splitDate();
      return this.selectedInputType = 'date';
    }

    if (this.type === FieldInputType.time) {
      if (this.prefillEmptyTimeWithNow && ValidationUtil.isStringEmpty(this.value)) {
        this.value = DateUtil.getCurrentTime24();
        this.onValueChange();
        this.doValidation();
      }
      return this.selectedInputType = 'time';
    }

    return this.selectedInputType = 'input';
  }

  private computeOptions() {
    this.showDialCodeSelector = this.type === FieldInputType.phone;
    this.showDropdown =
      (this.type === FieldInputType.country || this.type === FieldInputType.dropdown) &&
      !this.checkBoxRequired;
    this.displayOptions = this.getOptions();
    this.isLargeList = (this.displayOptions?.length ?? 0) > 5;
  }

  private computeSelectedLabel() {
    this.selectedLabel = this.getDropdownSelection(this.value);
  }

  async openCountryCodeSelector() {
    if (!ValidationUtil.isStringEmpty(this.fixedCountry)) {
      return this.showCountryUnavailable();
    }

    const modal = await this.modalController.create({
      component: SuperSelectComponent,
      componentProps: {
        options: this.getOptions(),
        title: this.label
      }
    });
    await modal.present();
    const res = await modal.onDidDismiss();
    if (res?.data?.value) {
      this.countryCode = res.data.value;
      this.countryCodeChange.emit(this.countryCode);
      this.cdr.markForCheck();
    }
  }

  showCountryUnavailable() {
    this.displayService.alert(
      `Only Phone Numbers issued by ${this.fixedCountry} are supported`,
      'You can continue with your email address'
    );
  }

  onValueChange(value?: string) {
    this.valueChange.emit(value ?? this.value);
    this.computeSelectedLabel();
  }

  doValidation() {
    if (!this.validate) {
      return;
    }
    this.publishError(null);

    if (this.required && ValidationUtil.isStringEmpty(this.value)) {
      this.publishError('Required');
      return;
    }

    if (ValidationUtil.isStringEmpty(this.value)) {
      return;
    }

    switch (this.type) {
      case FieldInputType.phone:
        return ValidationUtil.isValidPhone(this.countryCode + this.phoneInput)
          ? null
          : this.publishError('Please enter a valid phone number');
      case FieldInputType.email:
        this.value = TextUtil.removeAllSpaces(this.value?.toString());
        this.onValueChange();
        return ValidationUtil.isEmailValid(this.value)
          ? null
          : this.publishError('Please enter a valid email');
    }
  }

  private publishError(error: string | null) {
    this.errorNote = error;
    this.errorNoteChange.emit(this.errorNote);
  }

  getOptions() {
    if (this.type === FieldInputType.country) {
      return TextUtil.getCountryNameList();
    }
    if (this.type === FieldInputType.phone) {
      return getDialCodes();
    }
    if (!this.options) {
      return [];
    }
    if (Array.isArray(this.options)) {
      return this.options;
    }

    let o: string[] = this.options.split(',');
    return o.map((x) => {
      return x.replace(/^\s+/g, '');
    });
  }

  getLabel(option: any) {

    if (typeof option === 'string' || option instanceof String) {
      return option;
    }

    if (this.dropDownLabelKey) {
      return option[this.dropDownLabelKey];
    }

  }

  async updateWithModal() {
    const component = await SuperInputModalComponent();
    const modal = await this.modalController.create({
      component: component,
      componentProps: {
        title: this.label,
        action: (value) => {
          if (this.fieldCallback) { this.fieldCallback(value); }
        },
        inputValue: this.value,
        placeholder: this.placeholder
      },
      keyboardClose: false,
      animated: false
    });
    await modal.present();
    const res = await modal.onDidDismiss();
    const newVal = res?.data?.data;
    if (newVal !== undefined) {
      this.value = newVal;
      this.onValueChange();
      this.doValidation();
      this.cdr.markForCheck();
    }
  }

  onFieldClicked() {
    if (this.fieldCallback) {
      this.updateWithModal();
    }
  }

  async openSuperSelect() {
    if (this.readonly) {
      return;
    }
    const modal = await this.modalController.create({
      component: SuperSelectComponent,
      componentProps: {
        options: this.getOptions(),
        title: this.label,
        labelKey: this.dropDownLabelKey
      }
    });
    await modal.present();
    const res = await modal.onDidDismiss();
    if (res?.data?.value) {
      this.setDate(res.data.value);
      this.cdr.markForCheck();
    }
  }

  private setDate(date) {
    this.value = date;
    this.onValueChange();
    this.doValidation();
  }

  getDropdownSelection(value: any) {
    if (!value) {
      return;
    }
    if (ValidationUtil.isStringEmpty(this.dropDownLabelKey)) {
      return value;
    }
    return value[this.dropDownLabelKey];
  }

  public onFocusChanges(isFocused: boolean) {
    this.isFocused = isFocused;
  }

  splitPhoneNumber(): void {
    if (ValidationUtil.isStringEmpty(this.value)) {
      this.phoneInput = '';
      return;
    }
    const phone = parsePhoneNumberFromString('+' + this.value);
    if (phone) {
      this.countryCode = '+' + phone.countryCallingCode;
      this.phoneInput = phone.nationalNumber;
    }
  }

  onPhoneInputChange() {
    this.doValidation();
    const formattedPhoneNumber = this.countryCode.replace('+', '') + this.phoneInput;
    this.valueChange.emit(formattedPhoneNumber);
  }

  /** Populate the DD / MM / YYYY segments from the canonical `yyyy-MM-dd` value. */
  splitDate(): void {
    this.dateDay = '';
    this.dateMonth = '';
    this.dateYear = '';
    if (ValidationUtil.isStringEmpty(this.value)) {
      return;
    }
    const parts = String(this.value).split('-');
    if (parts.length === 3) {
      this.dateYear = parts[0];
      this.dateMonth = parts[1];
      this.dateDay = parts[2];
    }
  }

  /** Keep segments numeric, auto-advance to the next one, then re-assemble. */
  onDateSegmentInput(segment: 'day' | 'month' | 'year', next?: HTMLInputElement): void {
    this.dateDay = this.dateDay.replace(/\D/g, '').slice(0, 2);
    this.dateMonth = this.dateMonth.replace(/\D/g, '').slice(0, 2);
    this.dateYear = this.dateYear.replace(/\D/g, '').slice(0, 4);

    if (segment === 'day' && this.dateDay.length === 2 && next) {
      next.focus();
    }
    if (segment === 'month' && this.dateMonth.length === 2 && next) {
      next.focus();
    }

    this.assembleDate();
  }

  private assembleDate(): void {
    // Nothing entered yet -> keep the value empty (never default to today).
    if (!this.dateDay && !this.dateMonth && !this.dateYear) {
      this.value = '';
      this.onValueChange();
      this.doValidation();
      return;
    }

    // Only commit once all three segments are complete and form a real date.
    if (this.dateDay.length >= 1 && this.dateMonth.length >= 1 && this.dateYear.length === 4) {
      const day = this.dateDay.padStart(2, '0');
      const month = this.dateMonth.padStart(2, '0');
      const iso = `${this.dateYear}-${month}-${day}`;
      if (this.isRealDate(this.dateYear, month, day)) {
        this.value = iso;
        this.onValueChange();
        this.publishError(null);
      } else {
        this.value = '';
        this.onValueChange();
        if (this.validate) {
          this.publishError('Please enter a valid date');
        }
      }
    }
  }

  private isRealDate(year: string, month: string, day: string): boolean {
    const y = Number(year);
    const m = Number(month);
    const d = Number(day);
    if (m < 1 || m > 12 || d < 1 || d > 31) {
      return false;
    }
    const date = new Date(y, m - 1, d);
    return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d;
  }

  onNativeDatePicked(picked: string): void {
    if (!picked) {
      return;
    }
    this.value = picked;
    this.splitDate();
    this.onValueChange();
    this.doValidation();
    this.cdr.markForCheck();
  }
}
